import { useState, useCallback } from 'react';
import {
  MaterialMean,
  MaterialMeanResponse,
  SearchMaterialMeansRequest,
  ACLAuthorizationRequest,
  SearchMaterialMeansPostContentTypeEnum,
  CreateMaterialMeanContentTypeEnum,
} from '@/api/bol';
import { useSnackbarNotification } from '@/hooks/useSnackbarNotification';
import { getMaterialMeansApi } from '@/services/bolApiClient';
import { extractErrorMessage, formatOperationError } from '@/utils/errorUtils';
import { getLogisticsObjectDetailPath } from '@/types/logisticsObject';
import { getEffectiveLogisticsObjectSearchQuery } from '@/utils/searchDefaults';

export interface UseMaterialMeansReturn {
  materialMeans: MaterialMeanResponse[];
  loading: boolean;
  searchError: string | null;
  selectedMaterialMean: MaterialMeanResponse | null;
  setSelectedMaterialMean: (materialMean: MaterialMeanResponse | null) => void;
  fetchMaterialMeans: (page?: number, pageSize?: number, searchBody?: SearchMaterialMeansRequest) => Promise<boolean>;
  createMaterialMean: (materialMeanData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => Promise<void>;
  deleteMaterialMean: (materialMeanId: string) => Promise<void>;
  setTotal: (total: number) => void;
  total: number;
}

export const useMaterialMeans = (): UseMaterialMeansReturn => {
  const [items, setItems] = useState<MaterialMeanResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selectedMaterialMean, setSelectedMaterialMean] = useState<MaterialMeanResponse | null>(null);
  const [total, setTotal] = useState(0);

  const { showSuccess, showError } = useSnackbarNotification();

  const fetchMaterialMeans = useCallback(async (page: number = 0, pageSize: number = 10, searchBody?: SearchMaterialMeansRequest): Promise<boolean> => {
    const normalizedSearchBody = getEffectiveLogisticsObjectSearchQuery(searchBody);
    setLoading(true);
    setSearchError(null);

    try {
      const api = getMaterialMeansApi();
      const response = await api.searchMaterialMeansPost({
        contentType: SearchMaterialMeansPostContentTypeEnum.ApplicationJson,
        searchMaterialMeansRequest: normalizedSearchBody,
        limit: pageSize,
        offset: page * pageSize,
      });

      setItems(response.results || []);
      setTotal(response.pagination?.total || 0);
      return true;
    } catch (err: unknown) {
      const errorMessage = await extractErrorMessage(err, 'Failed to fetch material-means');
      setSearchError(errorMessage);
      showError(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [showError]);

  const createMaterialMean = useCallback(async (materialMeanData: unknown, memberIdentifier: string, aclAuthorizations?: ACLAuthorizationRequest[]) => {
    try {
      const api = getMaterialMeansApi();

      const createdMaterialMean = await api.createMaterialMean({
        contentType: CreateMaterialMeanContentTypeEnum.ApplicationJson,
        xBOLMemberIdentifier: memberIdentifier,
        materialMean: materialMeanData as MaterialMean,
        source: true,
        xBOLACLAuthorizations: aclAuthorizations ? JSON.stringify(aclAuthorizations) : undefined,
      });

      const createdMaterialMeanId = createdMaterialMean?.id;

      showSuccess(
        'MaterialMean created successfully',
        createdMaterialMeanId
          ? {
              link: {
                label: 'View details',
                href: getLogisticsObjectDetailPath('material-mean', createdMaterialMeanId),
              },
            }
          : undefined,
      );
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Create material-mean', 'Failed to create material-mean');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  const deleteMaterialMean = useCallback(async (materialMeanId: string) => {
    try {
      const api = getMaterialMeansApi();
      await api.deleteMaterialMeanById({ materialMeanId });
      showSuccess('MaterialMean deleted successfully');
    } catch (err: unknown) {
      const errorMessage = await formatOperationError(err, 'Delete material-mean', 'Failed to delete material-mean');
      showError(errorMessage);
      throw err;
    }
  }, [showSuccess, showError]);

  return {
    materialMeans: items,
    loading,
    searchError,
    selectedMaterialMean,
    setSelectedMaterialMean,
    fetchMaterialMeans,
    createMaterialMean,
    deleteMaterialMean,
    setTotal,
    total,
  };
};
